import React, { useEffect, useRef, useState } from 'react';
import { Checkbox } from '@mui/material';
import SignatureCanvas from 'react-signature-canvas';

/**
 * Draw — Pure canvas signature drawing UI.
 *
 * Renders the signature canvas, clear button, and "save as default" checkbox.
 * Does NOT handle upload or dispatch — parent (SignaturePanel) does that.
 *
 * @param {Function} onCanvasRef            - Ref callback to expose sigCanvas
 * @param {Function} [onSaveAsDefaultChange] - Called when "save as default" checkbox changes
 * @param {Function} [onGeoChange]          - Called when geolocation changes
 * @param {Function} [onDrawingChange]      - Called when drawing state changes (user starts/clears)
 * @param {Function} [onDrawEnd]            - Called when user finishes a stroke (canvas onEnd)
 * @param {boolean}  [hideSaveDefault]       - Hides the "Save as my default signature" checkbox
 */
export const Draw = ({ onCanvasRef, onSaveAsDefaultChange, onGeoChange, onDrawingChange, canvasWidth, onDrawEnd, hideSaveDefault }) => {
    const [isDrawing, setIsDrawing] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [saveAsDefault, setSaveAsDefault] = useState(false);
    const sigCanvas = useRef(null);

    useEffect(() => {
        setIsOpen(true);
        if (onCanvasRef) onCanvasRef(sigCanvas);
    }, []);

    // Geolocation capture — passed up to parent for use during submit
    useEffect(() => {
        if (!isOpen) return;

        if (!navigator.geolocation) {
            console.error('Geolocation is not supported by your browser.');
            return;
        }

        const watchId = navigator.geolocation.watchPosition(
            (pos) => {
                const coords = {
                    lat: pos.coords.latitude,
                    lng: pos.coords.longitude,
                };
                onGeoChange?.(coords);
            },
            (error) => {
                if (error.code === error.PERMISSION_DENIED) {
                    onGeoChange?.(null);
                }
            }
        );

        return () => {
            navigator.geolocation.clearWatch(watchId);
        };
    }, [isOpen]);

    const clearSignature = () => {
        if (sigCanvas?.current) {
            sigCanvas.current.clear();
            setIsDrawing(false);
            onDrawingChange?.(false);
        }
    };

    const handleMouseDown = () => {
        setIsDrawing(true);
        onDrawingChange?.(true);
    };

    const handleSaveAsDefaultChange = (e) => {
        setSaveAsDefault(e.target.checked);
        onSaveAsDefaultChange?.(e.target.checked);
    };

    return (
        <div>
            <div className="signature-upload-draw-box">
                <div className="canvas-wrapper">
                    <SignatureCanvas
                        ref={sigCanvas}
                        penColor="black"
                        canvasProps={{ width: canvasWidth ? canvasWidth : 480, height: 100, className: 'sigCanvas' }}
                        onBegin={handleMouseDown}
                        onEnd={() => onDrawEnd?.()}
                    />
                    <div className="canvas-line" />
                    <div className="type-signature-actions">
                        <div style={{ width: '25%' }} />
                        {!hideSaveDefault && (
                            <div className="type-signature-save-default" style={{ width: '50%' }}>
                                <Checkbox
                                    checked={saveAsDefault}
                                    onChange={handleSaveAsDefaultChange}
                                    style={{ padding: 0, color: saveAsDefault ? '#347AF0' : '#D0D5DD' }}
                                />
                                <span>Save as my default signature</span>
                            </div>
                        )}
                        {hideSaveDefault && <div style={{ width: '50%' }} />}
                        <div style={{ width: '25%', textAlign: 'end' }}>
                            {isDrawing && (
                                <button className="type-signature-clear-btn" onClick={clearSignature}>
                                    Clear
                                </button>
                            )}
                        </div>
                    </div>
                </div>
                {/*<p className="draw-info-text">By signing electronically, I agree to the clinic's terms and conditions.</p>*/}
            </div>
        </div>
    );
};
